import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import {
  situationTabLabels,
  situationTabOrder,
  situationTabsWithoutBadge,
  type SituationTab,
} from '@/packages/academic-hub/constants';

interface SituationTabsProps {
  value: SituationTab;
  counts: Record<SituationTab, number>;
  onChange: (tab: SituationTab) => void;
}

const CountBadge = ({ count, active }: { count: number; active: boolean }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 20,
      height: 20,
      px: 0.5,
      borderRadius: '50%',
      backgroundColor: active ? 'neutral.darker' : 'neutral.dark',
      color: 'neutral.0',
      fontSize: 12,
      fontWeight: 600,
    }}
  >
    {count}
  </Box>
);

export const SituationTabs = ({
  value,
  counts,
  onChange,
}: SituationTabsProps) => (
  <Tabs
    value={value}
    onChange={(_, tab) => onChange(tab)}
    sx={{
      width: '100%',
      '& .MuiTabs-scroller::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        borderBottom: ({ palette }) => `1px solid ${palette.neutral.light}`,
      },
      '& .MuiTabs-indicator': {
        display: 'block',
        backgroundColor: 'neutral.darker',
        height: 2,
        bottom: 0,
        zIndex: 1,
      },
    }}
  >
    {situationTabOrder.map((situationTab) => {
      const active = situationTab === value;
      const count = counts[situationTab];
      const showBadge =
        count > 0 && !situationTabsWithoutBadge.includes(situationTab);

      return (
        <Tab
          key={situationTab}
          value={situationTab}
          disableRipple
          sx={{
            minHeight: 'auto',
            border: 'none',
            borderRadius: 0,
            backgroundColor: 'transparent',
            px: 2,
            py: 1.5,
            '&:not(:first-of-type)': { borderLeft: 'none' },
            '&.Mui-selected': {
              border: 'none',
              backgroundColor: 'transparent',
            },
          }}
          label={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: active ? 600 : 400,
                  color: active ? 'neutral.darker' : 'neutral.dark',
                }}
              >
                {situationTabLabels[situationTab]}
              </Typography>
              {showBadge && <CountBadge count={count} active={active} />}
            </Stack>
          }
        />
      );
    })}
  </Tabs>
);
